import React from "react";
import { Globe, Loader2, Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLang } from "@/context/LanguageContext";

/**
 * Language switcher — supports all 22 Indian scheduled languages + English.
 * Compact variant is used inside the AppShell top bar; expanded variant on the
 * landing header.
 */
export default function LanguageSwitcher({ variant = "compact" }) {
  const { lang, setLang, languages, loading } = useLang();
  const current = languages.find((l) => l.code === lang) || { code: "en", label: "English", native: "English" };

  const trigger = variant === "expanded" ? (
    <button
      className="inline-flex items-center gap-2 rounded-md border border-border bg-white/70 backdrop-blur-sm px-3 py-1.5 text-sm hover:border-primary transition-colors"
      data-testid="language-switcher-trigger"
    >
      <Globe size={14} className="text-primary" />
      <span className="font-medium text-primary">{current.native}</span>
      {loading && <Loader2 size={12} className="animate-spin text-muted-foreground" />}
    </button>
  ) : (
    <button
      className="inline-flex items-center gap-1.5 rounded-md hover:bg-muted px-2 py-1.5 text-sm"
      title={`Language: ${current.label}`}
      data-testid="language-switcher-trigger"
    >
      <Globe size={14} className="text-primary" />
      <span className="text-primary font-medium hidden md:inline">{current.native}</span>
      {loading && <Loader2 size={11} className="animate-spin text-muted-foreground" />}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 max-h-[70vh] overflow-y-auto"
        data-testid="language-switcher-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Language · भाषा
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {languages.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={(e) => { e.preventDefault(); setLang(l.code); }}
            className="flex items-center justify-between gap-2 cursor-pointer"
            data-testid={`language-option-${l.code}`}
          >
            <div className="min-w-0">
              <div className="font-medium text-primary truncate">{l.native}</div>
              <div className="text-[11px] text-muted-foreground">{l.label}</div>
            </div>
            {l.code === lang && <Check size={14} className="text-primary shrink-0" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <div className="px-2 py-2 text-[10px] text-muted-foreground leading-relaxed">
          Translations are AI-generated. Please verify important medical or legal information in English.
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
